import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useNavigate } from "react-router-dom";
import { fetchUsersAction } from "../../ustils/actioncreator";
import AdminTabBar from "./AdminTabBar";
import { useAdminUi } from "../../hooks/useAdminUi";

const ROLE_FILTER = {
  ALL: 0,
  USER: 1,
  BROKER: 2,
};

function toNumber(value, fallback = null) {
  if (value === undefined || value === null || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function unwrapPayload(res) {
  if (!res || typeof res !== "object") return {};
  const root = res.data && typeof res.data === "object" && !Array.isArray(res.data) ? res.data : res;
  return root?.data !== undefined && typeof root.data === "object" ? root.data : root;
}

function extractUserList(payload) {
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload)) return payload;
  return [];
}

function extractTotal(payload, res, listLength, { useListLengthFallback = true } = {}) {
  const p = Array.isArray(payload) ? {} : payload || {};
  const r = res && typeof res === "object" ? res : {};
  const nestedObj =
    r.data && typeof r.data === "object" && !Array.isArray(r.data) ? r.data : null;
  const candidates = [
    p.total,
    p.total_count,
    p.totalCount,
    p.count,
    p.pagination?.total,
    p.pagination?.total_count,
    p.pagination?.totalCount,
    p.meta?.total,
    p.meta?.total_count,
    r.totalRecords,
    r.total,
    r.total_count,
    r.totalCount,
    nestedObj?.total,
    nestedObj?.total_count,
    nestedObj?.totalCount,
    nestedObj?.totalRecords,
  ];
  for (const c of candidates) {
    const n = toNumber(c, null);
    if (n !== null && n >= 0) return n;
  }
  return useListLengthFallback ? listLength : null;
}

function countRolesInList(records) {
  let userCount = 0;
  let brokerCount = 0;
  for (const u of records) {
    const roleVal = u?.role ?? u?.role_id ?? u?.user_role;
    if (roleVal === 2 || roleVal === "2") brokerCount += 1;
    else if (roleVal === 1 || roleVal === "1") userCount += 1;
    else if (u?.isBrokker === true) brokerCount += 1;
  }
  return { users: userCount, brokers: brokerCount };
}

function extractRoleCounts(payload, fallbackTotal) {
  const stats = payload?.counts || payload?.stats || payload?.role_counts || payload?.roleCounts;
  if (!stats || typeof stats !== "object") return null;
  const all =
    toNumber(stats.all, null) ??
    toNumber(stats.total, null) ??
    toNumber(stats.all_count, null) ??
    toNumber(stats.allCount, null) ??
    (fallbackTotal != null ? fallbackTotal : null);
  const user =
    toNumber(stats.user, null) ??
    toNumber(stats.users, null) ??
    toNumber(stats.user_count, null) ??
    toNumber(stats.userCount, null);
  const broker =
    toNumber(stats.broker, null) ??
    toNumber(stats.brokers, null) ??
    toNumber(stats.broker_count, null) ??
    toNumber(stats.brokerCount, null);
  if (all === null && user === null && broker === null) return null;
  return { all, user, broker };
}

function ActionIcons() {
  const { ui } = useAdminUi();
  return (
    <Stack direction="row" spacing={0.8}>
      <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16, color: ui.iconView, cursor: "pointer" }} />
      <EditOutlinedIcon sx={{ fontSize: 16, color: ui.iconEdit, cursor: "pointer" }} />
      <DeleteOutlineOutlinedIcon sx={{ fontSize: 16, color: ui.iconDelete, cursor: "pointer" }} />
    </Stack>
  );
}

function mapUserToRow(user) {
  const name = user?.name || user?.full_name || "—";
  const email = user?.email || user?.professionalEmail || "—";
  const country = user?.countryCode != null ? String(user.countryCode).trim() : "";
  const mobile = user?.mobileNumber || user?.mobile || user?.phone || "";
  const phone = [country, mobile].filter(Boolean).join(" ").trim() || "—";
  const cities = Array.isArray(user?.dealingCities) ? user.dealingCities.filter(Boolean).join(", ") : "";
  const location =
    cities ||
    user?.dealingCountry ||
    user?.location ||
    user?.address ||
    user?.city ||
    "—";
  const dateRaw = user?.created_at || user?.createdAt || user?.updated_at;
  const date = dateRaw ? new Date(dateRaw).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
  const roleNum = user?.role ?? user?.role_id ?? user?.user_role;
  let roleLabel = "User";
  if (roleNum === 2 || roleNum === "2") roleLabel = "Broker";
  else if (roleNum === 1 || roleNum === "1") roleLabel = "User";
  else if (roleNum === 0 || roleNum === "0") roleLabel = "All";
  else if (user?.isBrokker === true) roleLabel = "Broker";
  const avatarUrl = user?.userProfileImage || user?.profileImage || user?.avatar || null;
  return { id: user?._id || user?.id, name, email, phone, location, date, roleLabel, avatarUrl, raw: user };
}

export default function ClientsList() {
  const {
    listHeaderSx,
    listPaperSx,
    listTitleSx,
    primaryBtnSx,
    tableBodyCellSx,
    tableHeaderCellSx,
    tableRowHoverSx,
    tableSx,
    tableContainerSx,
  } = useAdminUi();
  const navigate = useNavigate();
  const [roleFilter, setRoleFilter] = useState(ROLE_FILTER.ALL);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: null, user: null, broker: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadRoleTotals = useCallback(async () => {
    try {
      const headRes = await fetchUsersAction({ page: 1, perPage: 1, role: ROLE_FILTER.ALL });
      const pHead = unwrapPayload(headRes);
      const listHead = extractUserList(pHead);
      const explicitTotal = extractTotal(pHead, headRes, listHead.length, {
        useListLengthFallback: false,
      });
      const allTotal =
        explicitTotal ??
        extractTotal(pHead, headRes, listHead.length, { useListLengthFallback: true });

      const fromStats = extractRoleCounts(pHead, allTotal);
      if (
        fromStats &&
        fromStats.all != null &&
        fromStats.user != null &&
        fromStats.broker != null
      ) {
        setCounts({
          all: fromStats.all,
          user: fromStats.user,
          broker: fromStats.broker,
        });
        return;
      }

      const cap =
        explicitTotal != null
          ? Math.min(Math.max(explicitTotal, listHead.length, 1), 500)
          : Math.min(500, Math.max(listHead.length, 1));
      const fullRes = await fetchUsersAction({ page: 1, perPage: cap, role: ROLE_FILTER.ALL });
      const pFull = unwrapPayload(fullRes);
      const listFull = extractUserList(pFull);
      const all =
        extractTotal(pFull, fullRes, listFull.length, { useListLengthFallback: false }) ??
        extractTotal(pFull, fullRes, listFull.length, { useListLengthFallback: true }) ??
        allTotal;

      const { users: userN, brokers: brokerN } = countRolesInList(listFull);
      if (all != null && listFull.length >= all) {
        setCounts({ all, user: userN, broker: brokerN });
        return;
      }

      const [resUser, resBroker] = await Promise.all([
        fetchUsersAction({ page: 1, perPage: 1, role: ROLE_FILTER.USER }),
        fetchUsersAction({ page: 1, perPage: 1, role: ROLE_FILTER.BROKER }),
      ]);
      const t1 = extractTotal(unwrapPayload(resUser), resUser, 1, { useListLengthFallback: false });
      const t2 = extractTotal(unwrapPayload(resBroker), resBroker, 1, { useListLengthFallback: false });
      setCounts({
        all: all ?? listFull.length,
        user: t1 != null ? t1 : userN,
        broker: t2 != null ? t2 : brokerN,
      });
    } catch {
    }
  }, []);

  useEffect(() => {
    loadRoleTotals();
  }, [loadRoleTotals]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchUsersAction({ page, perPage, role: roleFilter });
      const payload = unwrapPayload(res);
      const list = extractUserList(payload);
      setRows(list.map(mapUserToRow));
      const metaTotal =
        extractTotal(payload, res, list.length, { useListLengthFallback: false }) ??
        extractTotal(payload, res, list.length, { useListLengthFallback: true });
      setTotal(metaTotal);

      const fromPayload = extractRoleCounts(payload, metaTotal);
      if (
        fromPayload &&
        fromPayload.user != null &&
        fromPayload.broker != null &&
        fromPayload.all != null
      ) {
        setCounts({
          all: fromPayload.all,
          user: fromPayload.user,
          broker: fromPayload.broker,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load clients.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, roleFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterClick = (role) => {
    setRoleFilter(role);
    setPage(1);
  };

  const allCount = counts.all ?? total ?? rows.length;
  const userCount = counts.user ?? "—";
  const brokerCount = counts.broker ?? "—";
  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage) || 1);
  const canGoNext = page < totalPages && rows.length > 0;

  return (
    <Paper elevation={0} sx={listPaperSx}>
      <Box
        sx={{
          ...listHeaderSx,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Typography sx={listTitleSx}>Client List</Typography>
          <AdminTabBar
            tabs={[
              { id: String(ROLE_FILTER.ALL), label: `All · ${allCount ?? "—"}` },
              { id: String(ROLE_FILTER.USER), label: `User · ${userCount}` },
              { id: String(ROLE_FILTER.BROKER), label: `Broker · ${brokerCount}` },
            ]}
            value={String(roleFilter)}
            onChange={(id) => handleFilterClick(Number(id))}
          />
        </Stack>
        <Button variant="contained" color="primary" sx={primaryBtnSx}>
          + Add User
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ m: 1 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellSx}>Customer Photo & Name</TableCell>
                <TableCell sx={tableHeaderCellSx}>Role</TableCell>
                <TableCell sx={tableHeaderCellSx}>Location</TableCell>
                <TableCell sx={tableHeaderCellSx}>Email</TableCell>
                <TableCell sx={tableHeaderCellSx}>Phone</TableCell>
                <TableCell sx={tableHeaderCellSx}>Date</TableCell>
                <TableCell sx={tableHeaderCellSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                    No clients found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    hover
                    key={r.id || r.email}
                    onClick={() => r.id && navigate(`/dashboard/customers/overview/${r.id}/info`)}
                    sx={{ cursor: "pointer", ...tableRowHoverSx }}
                  >
                    <TableCell sx={tableBodyCellSx}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={r.avatarUrl || undefined}
                          sx={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}
                        >
                          {(r.name || "?")[0]}
                        </Avatar>
                        <Typography sx={{ fontSize: 14 }}>{r.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Chip label={r.roleLabel} size="small" sx={{ height: 26, fontSize: 14 }} />
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>{r.location}</TableCell>
                    <TableCell sx={tableBodyCellSx}>{r.email}</TableCell>
                    <TableCell sx={tableBodyCellSx}>{r.phone}</TableCell>
                    <TableCell sx={tableBodyCellSx}>{r.date}</TableCell>
                    <TableCell sx={tableBodyCellSx} onClick={(e) => e.stopPropagation()}>
                      <ActionIcons />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
          <Stack direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} flexWrap="wrap" spacing={1} sx={{ p: 1.2 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Typography sx={{ fontSize: 14, alignSelf: "center" }}>
              Page {page} {total ? `(${total} total)` : ""}
            </Typography>
            <Button size="small" disabled={!canGoNext} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  );
}
